import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, getAccessToken } = useAuth();
  const [status, setStatus] = useState('loading');
  const [data, setData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (authLoading) {
      return undefined;
    }

    if (!API || !sessionId) {
      setStatus('error');
      return undefined;
    }

    if (!user) {
      setStatus('auth');
      return undefined;
    }

    let cancelled = false;
    let timeoutId = null;
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      try {
        const token = await getAccessToken();
        const res = await fetch(`${API}/api/checkout/status/${sessionId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          const errorBody = await res.json().catch(() => ({}));
          throw new Error(errorBody.detail || 'Failed to check payment status.');
        }
        const payload = await res.json();
        if (cancelled) {
          return;
        }

        setData(payload);
        if (payload.payment_status === 'paid') {
          setStatus('success');
          return;
        }
        if (payload.status === 'expired') {
          setStatus('expired');
          return;
        }

        attempts += 1;
        if (attempts < maxAttempts) {
          timeoutId = window.setTimeout(poll, pollInterval);
        } else {
          setStatus('timeout');
        }
      } catch {
        if (!cancelled) {
          setStatus('error');
        }
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [authLoading, getAccessToken, sessionId, user]);

  return (
    <div className="min-h-screen flex items-center justify-center pt-20 pb-16" data-testid="payment-success-page">
      <div className="max-w-md w-full mx-4 text-center">
        {(status === 'loading' || authLoading) && (
          <div data-testid="payment-loading">
            <Loader2 className="w-10 h-10 animate-spin text-neutral-500 mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-medium text-white mb-2">Processing payment...</h2>
            <p className="text-sm text-neutral-500">Verifying your payment with Stripe.</p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="payment-success">
            <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-400" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-2xl font-medium text-white mb-2">Payment successful</h2>
            <p className="text-sm text-neutral-500 mb-2">
              Thank you for subscribing to Codelves {data?.metadata?.package_name || ''}.
            </p>
            <p className="text-xs text-neutral-600 mb-8">
              Your account has been updated in Firestore and should be available immediately.
            </p>
            <Link
              to="/account"
              className="inline-flex items-center justify-center rounded-md bg-white text-black px-6 py-3 text-sm font-medium hover:bg-neutral-200 transition-colors"
              data-testid="payment-goto-account"
            >
              Go to Account
            </Link>
          </div>
        )}

        {status === 'auth' && (
          <div data-testid="payment-auth-required">
            <div className="w-16 h-16 rounded-full bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-yellow-400" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-2xl font-medium text-white mb-2">Sign in to verify payment</h2>
            <p className="text-sm text-neutral-500 mb-8">
              Payment status is tied to your Firebase account. Sign back in, then reopen this page.
            </p>
            <Link
              to="/?auth=login"
              className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to sign in
            </Link>
          </div>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <div data-testid="payment-error">
            <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-400" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-2xl font-medium text-white mb-2">
              {status === 'expired' ? 'Session expired' : status === 'timeout' ? 'Verification timeout' : 'Something went wrong'}
            </h2>
            <p className="text-sm text-neutral-500 mb-8">
              {status === 'expired' ? 'Your payment session has expired. Please try again.' : 'We could not verify your payment. Please contact support if you were charged.'}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors"
              data-testid="payment-back-home"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
